/**
 * My Vendors request click lockdown (US 14688).
 * VQQ managers may see VQA rows but not open them; VQA managers vice versa.
 * Sponsor Admin and dual-role users keep access to both families.
 */

import { getRequestTypeFamily } from "./myVendorsRequestUtils";

export function normalizeMyVendorSubRoles(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (!item || typeof item !== "object") return "";
      return String(
        item.sub_role ??
          item.Subrole ??
          item.subrole ??
          item.role ??
          item.name ??
          ""
      ).trim();
    })
    .filter(Boolean);
}

export function hasMyVendorSponsorAdminRole(subRoles) {
  return normalizeMyVendorSubRoles(subRoles).includes("Sponsor Admin");
}

export function hasMyVendorVqqManagerRole(subRoles) {
  return normalizeMyVendorSubRoles(subRoles).some(
    (role) => role === "RFI Manager" || role.startsWith("RFI Manager")
  );
}

export function hasMyVendorVqaManagerRole(subRoles) {
  return normalizeMyVendorSubRoles(subRoles).some(
    (role) =>
      role === "VQA Manager" ||
      role.startsWith("VQA Manager") ||
      role === "Audit & Assessment Manager"
  );
}

/**
 * @returns {{ canOpenVqq: boolean, canOpenVqa: boolean }}
 */
export function getMyVendorRequestAccessFlags(subRoles) {
  const isSponsorAdmin = hasMyVendorSponsorAdminRole(subRoles);
  const isRfiManager = hasMyVendorVqqManagerRole(subRoles);
  const isVqaManager = hasMyVendorVqaManagerRole(subRoles);

  return {
    canOpenVqq: isSponsorAdmin || isRfiManager || !isVqaManager,
    canOpenVqa: isSponsorAdmin || isVqaManager || !isRfiManager,
  };
}

/**
 * RFI Details route bypass: My Vendors viewers may open VQQ details only when
 * their sub-roles allow VQQ (blocks VQA-only managers).
 */
export function canBypassRfiDetailsViaMyVendors(userRights, subRoles) {
  if (!Array.isArray(userRights) || !userRights.includes("My Vendors")) {
    return false;
  }
  return getMyVendorRequestAccessFlags(subRoles).canOpenVqq;
}

/**
 * Whether the current sponsor sub-roles may navigate/open a My Vendors request.
 * Visibility is unchanged — this only gates click-through to details/drawer.
 * A VQA only org opens no VQQs, whatever its users' roles.
 */
export function canOpenMyVendorRequest(request, subRoles, { isVqaOnly = false } = {}) {
  if (!request) return false;
  const family = getRequestTypeFamily(request.type);
  const { canOpenVqq, canOpenVqa } = getMyVendorRequestAccessFlags(subRoles);
  if (family === "VQA") return canOpenVqa;
  return !isVqaOnly && canOpenVqq;
}

/**
 * Shared copy for locked open/create actions (badges + Request New menu items).
 * @param {'VQQ'|'VQA'} family
 * @param {'open'|'create'} action
 */
export function getLockedMyVendorAccessTooltip(family, action = "open") {
  const roleLabel = family === "VQA" ? "VQA Manager" : "VQQ Manager";
  const productLabel = family === "VQA" ? "VQA" : "VQQ";
  if (action === "create") {
    return `${roleLabel} access is required to request a new ${productLabel}`;
  }
  return `${roleLabel} access is required to open this request`;
}

export function getLockedMyVendorRequestTooltip(request, { isVqaOnly = false } = {}) {
  const family = getRequestTypeFamily(request?.type);
  if (isVqaOnly && family === "VQQ") {
    return "This organization only requests VQAs, so its VQQs cannot be opened";
  }
  return getLockedMyVendorAccessTooltip(family, "open");
}

/** Same role gate as opening VQQ requests — used by Request New VQQ menu. */
export function canRequestNewMyVendorVqq(subRoles) {
  return getMyVendorRequestAccessFlags(subRoles).canOpenVqq;
}

/** Same role gate as opening VQA requests — used by Request New VQA menu. */
export function canRequestNewMyVendorVqa(subRoles) {
  return getMyVendorRequestAccessFlags(subRoles).canOpenVqa;
}
